import { Platform, Pressable, Text, View } from "react-native";
import type { designTokens } from "@amplecare/design-tokens";
import type { ClinicianWorkbenchPatientRecordV1 } from "@amplecare/contracts";

import { LineIcon, type LineIconName } from "../line-icons.js";
import type { AppShellTheme } from "../shell-core.js";
import {
  workforceOperationalLayout,
  workforceOperationalTypography,
} from "../workforce-design-system.js";
import {
  formatCaseTime,
  formatDateWithMonthName,
  formatPhoneDisplay,
  formatSexText,
} from "./record/format.js";
import { computeAgeYears } from "./record/scoring.js";
import {
  computePatientRecordReadiness,
  type PatientRecordSectionId,
} from "./record/readiness.js";

function startPhoneCall(phoneNumber: string): void {
  if (Platform.OS === "web" && typeof window !== "undefined") {
    window.location.href = `tel:${phoneNumber}`;
  }
}

function resolveRailControlHeight(): number {
  const usesTouchPointer =
    Platform.OS !== "web" ||
    (typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches);

  return usesTouchPointer
    ? workforceOperationalLayout.mobileControlHeight
    : workforceOperationalLayout.controlHeight;
}

const readinessIconNames: Record<PatientRecordSectionId, LineIconName> = {
  patient: "user",
  case: "case",
  contacts: "clinician",
  insurance: "document",
};

export interface PatientRecordRailInput {
  layout: (typeof designTokens)["semanticLayout"];
  onOpenPatientProfileSection: (section: PatientRecordSectionId) => void;
  pendingRedFlagCount: number;
  record: ClinicianWorkbenchPatientRecordV1;
  theme: AppShellTheme;
  tokens: typeof designTokens;
}

export function renderPatientRecordRailContent(input: PatientRecordRailInput) {
  const {
    layout,
    onOpenPatientProfileSection,
    pendingRedFlagCount,
    record,
    theme,
    tokens,
  } = input;
  const readiness = computePatientRecordReadiness(record);
  const railControlHeight = resolveRailControlHeight();
  const readinessFilledCount = readiness.reduce(
    (total, section) => total + section.filledCount,
    0,
  );
  const readinessTotalCount = readiness.reduce(
    (total, section) => total + section.totalCount,
    0,
  );
  const age = computeAgeYears(record.patient.dateOfBirth, null);
  const dobText = formatDateWithMonthName(record.patient.dateOfBirth);
  const identityParts = [
    record.patient.mrn ? `MRN ${record.patient.mrn}` : null,
    formatSexText(record.patient.sex),
    dobText ? `DOB ${dobText}${age != null ? ` (${age})` : ""}` : null,
  ].filter((part): part is string => part != null);
  const serviceParts = [
    record.case.dateOfService
      ? `DOS ${formatDateWithMonthName(record.case.dateOfService) ?? record.case.dateOfService}`
      : null,
    formatCaseTime(record.case.caseTime),
  ].filter((part): part is string => part != null);
  const procedureText =
    record.case.procedures.length > 0 ? record.case.procedures.join(", ") : null;
  const callChips = [
    {
      accessibleLabel: "cell",
      key: "cell",
      label: "Cell",
      phone: record.patient.primaryPhone.number,
    },
    {
      accessibleLabel: "alternate",
      key: "alt",
      label: "Alt",
      phone:
        record.patient.secondaryPhone.number ?? record.patient.homePhone.number,
    },
    {
      accessibleLabel: "escort",
      key: "escort",
      label: "Escort",
      phone: record.contacts.rideHome.phone,
    },
  ];

  const renderMetaLine = (
    text: string,
    options?: { emphasis?: "primary" | "secondary" },
  ) => (
    <Text
      key={text}
      numberOfLines={2}
      style={{
        color:
          options?.emphasis === "primary"
            ? theme.textPrimary
            : theme.textSecondary,
        fontFamily: layout.bodyFontFamily,
        fontSize:
          options?.emphasis === "primary"
            ? workforceOperationalTypography.body.fontSize
            : workforceOperationalTypography.metadata.fontSize,
        fontWeight: options?.emphasis === "primary" ? "700" : "600",
        lineHeight:
          options?.emphasis === "primary"
            ? workforceOperationalTypography.body.lineHeight
            : workforceOperationalTypography.metadata.lineHeight,
      }}
    >
      {text}
    </Text>
  );

  return (
    <View style={{ gap: tokens.spacing.md, paddingTop: tokens.spacing.sm }}>
      {pendingRedFlagCount > 0 ? (
        <View
          accessibilityLabel={`${pendingRedFlagCount} high-alert answer${pendingRedFlagCount === 1 ? " requires" : "s require"} review`}
          accessibilityLiveRegion="polite"
          accessibilityRole="alert"
          style={{
            alignItems: "center",
            backgroundColor: theme.statusChip.danger.background,
            borderColor: theme.statusChip.danger.border,
            borderRadius: workforceOperationalLayout.controlRadius,
            borderWidth: 1,
            flexDirection: "row",
            gap: tokens.spacing.sm,
            minHeight: railControlHeight,
            paddingHorizontal: tokens.spacing.sm,
            paddingVertical: 6,
          }}
        >
          <LineIcon
            color={theme.statusChip.danger.text}
            name="flag"
            size={16}
            strokeWidth={2.3}
          />
          <Text
            style={{
              color: theme.statusChip.danger.text,
              flex: 1,
              fontFamily: layout.bodyFontFamily,
              fontSize: workforceOperationalTypography.table.fontSize,
              fontWeight: "800",
              lineHeight: workforceOperationalTypography.table.lineHeight,
            }}
          >
            {`${String(pendingRedFlagCount)} high-alert answer${pendingRedFlagCount === 1 ? "" : "s"} to review`}
          </Text>
        </View>
      ) : null}

      <View style={{ gap: 2 }}>
        {identityParts.length > 0
          ? renderMetaLine(identityParts.join(" · "))
          : null}
        {procedureText
          ? renderMetaLine(procedureText, { emphasis: "primary" })
          : null}
        {serviceParts.length > 0
          ? renderMetaLine(serviceParts.join(" · "))
          : null}
        {record.case.surgeonName
          ? renderMetaLine(`Surgeon ${record.case.surgeonName}`)
          : null}
      </View>

      <View style={{ gap: 4 }}>
          <Text
            accessibilityRole="header"
            style={{
              color: theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: workforceOperationalTypography.metadata.fontSize,
              fontWeight: "800",
              lineHeight: workforceOperationalTypography.metadata.lineHeight,
              textTransform: "uppercase",
            }}
          >
            Phone
          </Text>
          <View
            style={{
              borderColor: theme.borderSubtle,
              borderRadius: workforceOperationalLayout.controlRadius,
              borderWidth: 1,
              overflow: "hidden",
            }}
          >
          {callChips.map((chip, chipIndex) => {
            const phoneDisplay = formatPhoneDisplay(chip.phone);
            const disabled = chip.phone == null;

            return (
              <Pressable
                accessibilityHint={
                  disabled ? undefined : "Starts a phone call"
                }
                accessibilityLabel={
                  disabled
                    ? `${chip.accessibleLabel} phone not captured`
                    : `Call ${chip.accessibleLabel} at ${phoneDisplay ?? chip.phone}`
                }
                accessibilityRole="button"
                accessibilityState={{ disabled }}
                aria-disabled={disabled}
                disabled={disabled}
                key={chip.key}
                onPress={() => {
                  if (chip.phone != null) {
                    startPhoneCall(chip.phone);
                  }
                }}
                style={({ pressed }) => ({
                  alignItems: "center",
                  backgroundColor: pressed
                    ? theme.backgroundSubtle
                    : theme.surface,
                  borderBottomColor: theme.borderSubtle,
                  borderBottomWidth:
                    chipIndex === callChips.length - 1 ? 0 : 1,
                  flexDirection: "row",
                  gap: tokens.spacing.sm,
                  minHeight: railControlHeight,
                  paddingHorizontal: tokens.spacing.sm,
                  paddingVertical: 4,
                })}
              >
                <LineIcon
                  color={disabled ? theme.textTertiary : theme.accentStrong}
                  name="phone"
                  size={16}
                  strokeWidth={2.25}
                />
                <Text
                  numberOfLines={1}
                  style={{
                    color: disabled
                      ? theme.textTertiary
                      : theme.textPrimary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: workforceOperationalTypography.table.fontSize,
                    fontWeight: "700",
                    lineHeight: workforceOperationalTypography.table.lineHeight,
                    width: 44,
                  }}
                >
                  {chip.label}
                </Text>
                <Text
                  numberOfLines={1}
                  style={{
                    color: disabled
                      ? theme.textTertiary
                      : theme.textSecondary,
                    flex: 1,
                    fontFamily: layout.bodyFontFamily,
                    fontSize:
                      workforceOperationalTypography.metadata.fontSize,
                    fontWeight: "600",
                    lineHeight:
                      workforceOperationalTypography.metadata.lineHeight,
                    minWidth: 0,
                  }}
                >
                  {phoneDisplay ?? "Not captured"}
                </Text>
                {disabled ? null : (
                  <LineIcon
                    color={theme.textTertiary}
                    name="chevron-right"
                    size={15}
                    strokeWidth={2.2}
                  />
                )}
              </Pressable>
            );
          })}
          </View>
      </View>

      <View style={{ gap: 4 }}>
        <View
          style={{
            alignItems: "center",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Text
            accessibilityRole="header"
            style={{
              color: theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: workforceOperationalTypography.metadata.fontSize,
              fontWeight: "800",
              lineHeight: workforceOperationalTypography.metadata.lineHeight,
              textTransform: "uppercase",
            }}
          >
            Profile readiness
          </Text>
          <Text
            style={{
              color: theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: workforceOperationalTypography.metadata.fontSize,
              fontWeight: "700",
              lineHeight: workforceOperationalTypography.metadata.lineHeight,
            }}
          >
            {`${readinessFilledCount}/${readinessTotalCount} fields`}
          </Text>
        </View>
        <View
          style={{
            borderColor: theme.borderSubtle,
            borderRadius: workforceOperationalLayout.controlRadius,
            borderWidth: 1,
            overflow: "hidden",
          }}
        >
          {readiness.map((section, sectionIndex) => {
            const isComplete = section.filledCount === section.totalCount;
            const isEmpty = section.filledCount === 0;
            const missingCount = section.totalCount - section.filledCount;
            const indicatorColor = isComplete
              ? theme.success
              : isEmpty
                ? theme.textTertiary
                : theme.warning;
            const readinessStateText = isComplete
              ? "Complete"
              : isEmpty
                ? "Not started"
                : `${missingCount} field${missingCount === 1 ? "" : "s"} missing`;

            return (
              <Pressable
                accessibilityHint="Opens this section in patient details"
                accessibilityLabel={`${section.titleText}. ${section.filledCount} of ${section.totalCount} fields complete. ${readinessStateText}.`}
                accessibilityRole="button"
                key={section.id}
                onPress={() => onOpenPatientProfileSection(section.id)}
                style={({ pressed }) => ({
                  alignItems: "center",
                  backgroundColor: pressed
                    ? theme.backgroundSubtle
                    : theme.surface,
                  borderBottomColor: theme.borderSubtle,
                  borderBottomWidth:
                    sectionIndex === readiness.length - 1 ? 0 : 1,
                  flexDirection: "row",
                  gap: tokens.spacing.sm,
                  minHeight: railControlHeight,
                  paddingHorizontal: tokens.spacing.sm,
                  paddingVertical: 4,
                })}
              >
                <View
                  style={{
                    alignItems: "center",
                    backgroundColor: isComplete
                      ? theme.statusChip.success.background
                      : theme.backgroundSubtle,
                    borderColor: indicatorColor,
                    borderRadius: workforceOperationalLayout.controlRadius,
                    borderWidth: 1,
                    height: 24,
                    justifyContent: "center",
                    width: 24,
                  }}
                >
                  <LineIcon
                    color={indicatorColor}
                    name={
                      isComplete ? "check" : readinessIconNames[section.id]
                    }
                    size={14}
                    strokeWidth={2.25}
                  />
                </View>
                <Text
                  numberOfLines={2}
                  style={{
                    color: theme.textPrimary,
                    flex: 1,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: workforceOperationalTypography.table.fontSize,
                    fontWeight: "700",
                    lineHeight: workforceOperationalTypography.table.lineHeight,
                    minWidth: 0,
                  }}
                >
                  {section.titleText}
                </Text>
                <Text
                  style={{
                    color: isComplete ? theme.success : theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize:
                      workforceOperationalTypography.metadata.fontSize,
                    fontWeight: "800",
                    lineHeight:
                      workforceOperationalTypography.metadata.lineHeight,
                  }}
                >
                  {`${section.filledCount}/${section.totalCount}`}
                </Text>
                <LineIcon
                  color={theme.textTertiary}
                  name="chevron-right"
                  size={15}
                  strokeWidth={2.2}
                />
              </Pressable>
            );
          })}
        </View>
      </View>
    </View>
  );
}
